import DataLayerService from "../../DataLayerService.js";
import { collectComponentAttributes } from "../common/component-attributes.js";

// GA4-Hard-Limits, an denen Events sonst mit 413 Payload Too Large fehlschlagen:
// - 16 KB pro Event (gesamt). Wir bleiben bei 12 KB für die items-Liste,
//   damit für die Event-Parameter (item_list_id, custom-Attribute, etc.) Platz bleibt.
// - 200 Einträge pro items-Array.
const MAX_ITEMS_PAYLOAD_BYTES = 12000;
const MAX_ITEMS_PER_EVENT = 200;

// TextEncoder liefert UTF-8-Bytes — String.length würde nur UTF-16-Code-Units
// zählen und Sonderzeichen (Umlaute, Emojis) systematisch unterschätzen.
const payloadEncoder = new TextEncoder();

/**
 * Handler für automatisches Tracking von Produkt- und Promotion-Impressionen
 *
 * Verwendet IntersectionObserver um zu erkennen, wann Produkte oder Promotions
 * im Viewport sichtbar werden und trackt diese automatisch als view_item_list
 * oder view_promotion Events.
 *
 * Unterstützt:
 * - Moderne Syntax: <dl-product> Elemente
 * - Legacy Syntax: [product-id] Attribute mit [impression] Container
 * - Promotions: [promotion-name] mit [promotion-creative]
 */
export default class ImpressionHandler {
    #type;
    #eventName;
    #queue = [];
    #observedEntries = new Set();
    #trackedEntries = new Set();
    #intersectionObserver = null;
    #mutationObserver = null;
    #batchSize;
    #datalayerService;
    #initialized = false;

    /**
     * Erstellt einen neuen ImpressionHandler
     * @param {string} type - "item" für Produkte oder "promotion" für Promotions
     * @param {number} batchSize - Anzahl Einträge vor automatischem Push (Standard: 25)
     * @param {DataLayerService} datalayerService - DataLayerService Instanz
     */
    constructor(type, batchSize = 25, datalayerService = new DataLayerService()) {
        this.#type = type;
        this.#eventName = type === 'item' ? 'view_item_list' : 'view_promotion';
        this.#batchSize = batchSize;
        this.#datalayerService = datalayerService;
    }

    /**
     * Initialisiert den Handler und startet Observer
     */
    init() {
        if (this.#initialized) {
            console.warn(`[ImpressionHandler:${this.#type}] Already initialized`);
            return;
        }

        this.#initialized = true;
        this.#setupIntersectionObserver();
        this.#setupMutationObserver();
        this.#scanEntries();
    }

    /**
     * Erstellt IntersectionObserver für Sichtbarkeitserkennung
     * @private
     */
    #setupIntersectionObserver() {
        const options = {
            threshold: 1 // 100% sichtbar
        };

        this.#intersectionObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.#handleEntryVisible(entry.target);
                }
            });
        }, options);
    }

    /**
     * Erstellt MutationObserver für dynamisch hinzugefügte Elemente
     * @private
     */
    #setupMutationObserver() {
        const options = {
            childList: true,
            subtree: true
        };

        this.#mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach(mutation => {
                mutation.addedNodes.forEach(node => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        this.#scanEntries();
                    }
                });
            });
        });

        this.#mutationObserver.observe(document.body, options);
    }

    /**
     * Scannt das DOM nach trackbaren Elementen
     * @private
     */
    #scanEntries() {
        const entries = this.#getEntries();
        if (entries) {
            entries.forEach(entry => this.#addEntryToTracking(entry));
        }
    }

    /**
     * Findet alle trackbaren Elemente basierend auf dem Typ
     * @private
     * @returns {NodeList|null}
     */
    #getEntries() {
        if (this.#type === 'item') {
            // Moderne Syntax: dl-product Elemente
            // Legacy Syntax: [impression] mit [product-id]
            return document.querySelectorAll('dl-product, [impression] [product-id], [impression][product-id]');
        } else if (this.#type === 'promotion') {
            // Promotion-Creative Elemente tracken
            // 1. promotion-creative innerhalb von promotion-name
            // 2. Element mit beiden Attributen
            return document.querySelectorAll('[promotion-name] [promotion-creative], [promotion-name][promotion-creative]');
        }
        return null;
    }

    /**
     * Fügt Element zum Tracking hinzu
     * @private
     * @param {Element} entry
     */
    #addEntryToTracking(entry) {
        if (this.#observedEntries.has(entry)) {
            return;
        }
        this.#observedEntries.add(entry);
        this.#intersectionObserver.observe(entry);
    }

    /**
     * Behandelt sichtbar gewordenes Element
     * @private
     * @param {Element} entry
     */
    #handleEntryVisible(entry) {
        const entryId = this.#getEntryId(entry);
        if (!entryId) return;

        const component = this.#findComponent(entry);
        const componentName = component ? component.getAttribute('component') : 'unknown';
        const productList = component ? component.getAttribute('product-list') || '' : '';

        if (this.#hasEntryBeenTracked(entryId, componentName, productList)) {
            return;
        }

        if (this.#type === 'promotion') {
            this.#dispatchPromotionImmediately(entry, component, componentName);
            this.#markEntryAsTracked(entryId, componentName, productList);
            return;
        }

        this.#addToQueue(entry);
    }

    /**
     * Ermittelt die ID eines Eintrags
     * @private
     * @param {Element} entry
     * @returns {string|null}
     */
    #getEntryId(entry) {
        if (this.#type === 'item') {
            // dl-product hat item-id oder item-name
            return entry.getAttribute('item-id') ||
                   entry.getAttribute('item_id') ||
                   entry.getAttribute('product-id') ||
                   entry.getAttribute('item-name') ||
                   entry.getAttribute('item_name');
        } else {
            // Promotions haben promotion-creative
            return entry.getAttribute('promotion-creative');
        }
    }

    /**
     * Fügt Element zur Warteschlange hinzu
     * @private
     * @param {Element} node
     */
    #addToQueue(node) {
        const nodeKey = this.#getEntryId(node);
        const component = this.#findComponent(node);
        const componentName = component ? component.getAttribute('component') : 'unknown';
        const productList = component ? component.getAttribute('product-list') || '' : '';

        const alreadyInQueue = this.#queue.some(
            queueEntry => queueEntry.nodeKey === nodeKey
                && queueEntry.componentName === componentName
                && queueEntry.productList === productList
        );

        if (alreadyInQueue) {
            return;
        }

        this.#queue.push({
            node,
            nodeKey,
            component,
            componentName,
            productList
        });

        if (this.#queue.length >= this.#batchSize) {
            this.#processQueue();
        }
    }

    /**
     * Findet die umschließende Komponente eines Elements
     * @private
     * @param {Element} entry
     * @returns {Element|null}
     */
    #findComponent(entry) {
        return entry.closest('[component]');
    }

    /**
     * Prüft ob Eintrag bereits getrackt wurde
     * @private
     * @param {string} entryId
     * @param {string} componentName
     * @param {string} productList
     * @returns {boolean}
     */
    #hasEntryBeenTracked(entryId, componentName, productList) {
        const trackingKey = this.#createTrackingKey(entryId, componentName, productList);
        return this.#trackedEntries.has(trackingKey);
    }

    /**
     * Markiert Eintrag als getrackt
     * @private
     * @param {string} entryId
     * @param {string} componentName
     * @param {string} productList
     */
    #markEntryAsTracked(entryId, componentName, productList) {
        const trackingKey = this.#createTrackingKey(entryId, componentName, productList);
        this.#trackedEntries.add(trackingKey);
    }

    /**
     * Erstellt eindeutigen Tracking-Schlüssel
     * @private
     * @param {string} entryId
     * @param {string} componentName
     * @param {string} productList
     * @returns {string}
     */
    #createTrackingKey(entryId, componentName, productList) {
        return `${entryId}-${componentName}-${productList}`;
    }

    /**
     * Verarbeitet die Warteschlange (nur für view_item_list — view_promotion
     * wird in #dispatchPromotionImmediately direkt versendet).
     * @private
     */
    #processQueue() {
        if (this.#queue.length === 0) return;

        const impressions = this.#prepareProductImpressions(this.#queue);

        for (const impression of impressions) {
            this.#createEvent(impression);
        }

        for (const queueEntry of this.#queue) {
            this.#markEntryAsTracked(queueEntry.nodeKey, queueEntry.componentName, queueEntry.productList || '');
        }

        this.#queue = [];
    }

    /**
     * Bereitet Produkt-Impressionen vor (gruppiert pro Component-Element).
     * Mehrere Komponenten mit gleichem `component`-Attribut, aber unterschiedlicher
     * `product-list`, erzeugen jeweils ein eigenes Event.
     * @private
     * @param {Array} queue
     * @returns {Array}
     */
    #prepareProductImpressions(queue) {
        const groups = new Map();

        for (const queueEntry of queue) {
            const { node, component, componentName, productList } = queueEntry;
            const groupKey = component || `__no-component__::${componentName}::${productList}`;

            if (!groups.has(groupKey)) {
                groups.set(groupKey, {
                    type: 'item',
                    node,
                    component,
                    componentName,
                    productList,
                    items: []
                });
            }

            const itemData = this.#getItemData(node);
            if (itemData) {
                groups.get(groupKey).items.push(itemData);
            }
        }

        return Array.from(groups.values());
    }

    /**
     * Pusht eine einzelne Promotion-Impression sofort.
     *
     * Promotion-Impressionen werden NICHT gebatcht:
     * 1. GA4 wertet bei view_promotion/select_promotion nur das erste Item im items-Array aus.
     * 2. Ein verspätetes view_promotion (per Batch oder Page-Unload) kann nach dem zugehörigen
     *    select_promotion ankommen und bricht damit die Attribution.
     *
     * @private
     * @param {Element} node
     * @param {Element|null} component
     * @param {string} componentName
     */
    #dispatchPromotionImmediately(node, component, componentName) {
        const itemsData = this.#collectPromotionItems(node);

        const impression = {
            type: 'promotion',
            node,
            component,
            componentName,
            creativeName: node.getAttribute('promotion-creative') || '',
            items: itemsData
        };

        this.#createEvent(impression);
    }

    /**
     * Sammelt die Items innerhalb einer Promotion (oder die Promotion selbst,
     * falls sie selbst Produkt-Attribute trägt).
     * @private
     * @param {Element} node
     * @returns {Array}
     */
    #collectPromotionItems(node) {
        const isPromotionItself = node.hasAttribute('product-id')
            || node.hasAttribute('item-id')
            || node.hasAttribute('item_id');

        const promotionItems = isPromotionItself
            ? [node]
            : node.querySelectorAll('dl-product, [product-id], [item-id], [item_id]');

        const itemsData = [];
        for (const item of promotionItems) {
            const itemData = this.#getItemData(item);
            if (itemData) {
                itemsData.push(itemData);
            }
        }
        return itemsData;
    }

    /**
     * Extrahiert Item-Daten aus einem Element
     * WICHTIG: Attribute werden OHNE Transformation übergeben,
     * damit DataLayerEvent die Normalisierung durchführen kann
     * @private
     * @param {Element} node
     * @returns {Object|null}
     */
    #getItemData(node) {
        const attributes = {};

        // Alle Attribute sammeln (OHNE Transformation - DataLayerEvent macht das!)
        Array.from(node.attributes).forEach(attr => {
            attributes[attr.name] = attr.value;
        });

        // Position bestimmen (falls in Komponente)
        const component = this.#findComponent(node);
        if (component) {
            attributes.component = component.getAttribute('component');

            // Product-list für item_list_name/item_list_id
            const productList = component.getAttribute('product-list');
            if (productList) {
                attributes['item-list-name'] = productList;
                attributes['item-list-id'] = component.getAttribute('component');
            }

            // Position automatisch bestimmen wenn nicht gesetzt
            if (!attributes.position && !attributes.index) {
                const position = this.#getPosition(node, component);
                if (position) {
                    attributes.index = position;
                }
            }
        }

        return Object.keys(attributes).length > 0 ? attributes : null;
    }

    /**
     * Bestimmt die Position eines Produkts in seiner Komponente
     * @private
     * @param {Element} element
     * @param {Element} component
     * @returns {number|null}
     */
    #getPosition(element, component) {
        const products = component.querySelectorAll('dl-product, [product-id], [item-id], [item_id]');
        let position = null;

        products.forEach((prod, index) => {
            if (prod === element) {
                position = index + 1;
            }
        });

        return position;
    }

    /**
     * Erstellt und sendet Event
     * @private
     * @param {Object} impression
     */
    #createEvent(impression) {
        const { component, componentName, creativeName, items } = impression;

        // view_item_list benötigt zwingend Items; view_promotion nicht (Items optional)
        if (this.#type === 'item' && items.length === 0) {
            return;
        }

        const params = this.#getEventParams(componentName, component, creativeName);

        try {
            this.#pushChunkedEvent(params, items);
        } catch (error) {
            console.error(`[ImpressionHandler:${this.#type}] Failed to push event:`, error);
        }
    }

    /**
     * Pusht das Event und splittet die items-Liste bei Bedarf in mehrere Pushes,
     * damit das 16-KB-Hard-Limit von GA4 nicht überschritten wird. Alle Chunks
     * teilen sich dieselben Event-Parameter (gleiche item_list_id/-name).
     * @private
     * @param {Object} params
     * @param {Array} items
     */
    #pushChunkedEvent(params, items) {
        const chunks = chunkItemsByPayloadSize(items);
        for (const chunk of chunks) {
            this.#datalayerService.pushEvent(this.#eventName, params, chunk);
        }
    }

    /**
     * Ermittelt Event-Parameter basierend auf Typ
     * @private
     * @param {string} componentName
     * @param {Element} component
     * @returns {Object}
     */
    #getEventParams(componentName, component, creativeName = '') {
        const params = {};

        if (this.#type === 'item') {
            const productList = component?.getAttribute('product-list') || componentName;
            params.item_list_id = componentName;
            params.item_list_name = productList;
        } else {
            // Promotion params
            const promotionName = component?.getAttribute('promotion-name') || '';
            params.promotion_id = componentName;
            params.promotion_name = promotionName;
            params.creative_name = creativeName;
        }

        // Sammle zusätzliche Attribute vom component-Element
        if (component) {
            const componentAttributes = collectComponentAttributes(component);
            Object.assign(params, componentAttributes);
        }

        return params;
    }

    /**
     * Verarbeitet verbleibende Einträge (vor Page-Unload)
     */
    processRemainingEntries() {
        if (this.#queue.length > 0) {
            this.#processQueue();
        }
    }

    /**
     * Stellt sicher, dass für eine Promotion bereits ein view_promotion-Event
     * abgesetzt wurde. Falls nicht, wird es synchron nachgeholt.
     *
     * Wird vom Click-Listener aufgerufen, bevor select_promotion gepusht wird:
     * GA4 verknüpft select_promotion mit dem zuvor gefeuerten view_promotion.
     * Wenn ein User klickt, bevor der IntersectionObserver getriggert hat,
     * würde sonst die Attribution brechen.
     *
     * @param {Element} node - Das [promotion-creative]-Element
     */
    ensurePromotionTracked(node) {
        if (this.#type !== 'promotion' || !node) {
            return;
        }
        this.#handleEntryVisible(node);
    }

    /**
     * Zerstört den Handler und gibt Ressourcen frei
     */
    destroy() {
        if (this.#intersectionObserver) {
            this.#intersectionObserver.disconnect();
        }
        if (this.#mutationObserver) {
            this.#mutationObserver.disconnect();
        }
        this.#observedEntries.clear();
        this.#queue = [];
        this.#trackedEntries.clear();
        this.#initialized = false;
    }
}

/**
 * Teilt eine items-Liste in Chunks, die das GA4-Payload-Limit nicht überschreiten.
 * Pathologisch große Einzel-Items (> MAX_ITEMS_PAYLOAD_BYTES) werden trotzdem
 * als eigener Chunk durchgereicht — das Event würde dann ggf. abgelehnt, aber
 * wir lassen den Aufrufer entscheiden statt das Item still zu verwerfen.
 *
 * @param {Array} items
 * @returns {Array<Array>}
 */
function chunkItemsByPayloadSize(items) {
    if (items.length === 0) {
        return [items];
    }

    const chunks = [];
    let currentChunk = [];
    let currentBytes = 0;

    for (const item of items) {
        const itemBytes = payloadEncoder.encode(JSON.stringify(item)).length;

        const exceedsByteLimit = currentBytes + itemBytes > MAX_ITEMS_PAYLOAD_BYTES;
        const exceedsCountLimit = currentChunk.length >= MAX_ITEMS_PER_EVENT;

        if (currentChunk.length > 0 && (exceedsByteLimit || exceedsCountLimit)) {
            chunks.push(currentChunk);
            currentChunk = [];
            currentBytes = 0;
        }

        currentChunk.push(item);
        currentBytes += itemBytes;
    }

    if (currentChunk.length > 0) {
        chunks.push(currentChunk);
    }

    return chunks;
}
